<template>
  <div class="flacons-modales">
    <b-modal :id="'flacon-ouvrir-' + _uid" v-model="modal_ouvrir" :title="'Ouvrir un flacon — ' + libelle" hide-footer
      :no-close-on-backdrop="is_saving" :no-close-on-esc="is_saving">
      <div class="fm-formulaire">
        <p class="fm-aide">Réserve : <strong>{{ reserve }}</strong> {{ reserve > 1 ? 'flacons' : 'flacon' }}. À l'ouverture, la réserve baisse de 1.
          Recopiez le lot et la péremption de l'étiquette.</p>
        <div class="fm-deux">
          <label>Lot
            <input v-model="form.lot" type="text" maxlength="60" :disabled="is_saving" placeholder="Ex. G2309" autocomplete="off">
          </label>
          <label>Date de péremption
            <input v-model="form.date_peremption" type="date" :min="aujourdhui" :disabled="is_saving">
          </label>
        </div>
        <label>Stabilité après ouverture (jours)
          <input v-model="form.stabilite_jours" type="number" min="1" max="3650" step="1" :disabled="is_saving" placeholder="Ex. 30">
          <small>Mémorisée pour ce réactif. Laissez vide si le fabricant n'indique rien.</small>
        </label>
        <label class="fm-case"><input v-model="form.cq_fait" type="checkbox" :disabled="is_saving"> Contrôle qualité fait sur ce flacon</label>
        <p class="fm-limite" role="status">{{ limite ? 'À utiliser avant le ' + dateCourte(limite) + '.' : 'Sans date limite : indiquez la péremption ou la stabilité.' }}</p>
        <p v-if="erreur" role="alert" class="fm-erreur">{{ erreur }}</p>
        <div class="ui-modal-actions fm-actions">
          <button type="button" class="btn btn-secondary-light-o btn-radius" :disabled="is_saving" @click="fermer">Annuler</button>
          <button type="button" class="btn btn-primary btn-radius" :disabled="is_saving" @click="enregistrerOuverture">{{ is_saving ? 'Enregistrement…' : 'Ouvrir le flacon' }}</button>
        </div>
      </div>
    </b-modal>

    <b-modal :id="'flacon-terminer-' + _uid" v-model="modal_terminer" :title="'Terminer le flacon — ' + libelle" hide-footer
      :no-close-on-backdrop="is_saving" :no-close-on-esc="is_saving">
      <div class="fm-formulaire">
        <p v-if="flacon" class="fm-aide">{{ flacon.resume }}</p>
        <fieldset class="fm-motifs" :disabled="is_saving">
          <legend>Pourquoi le flacon est-il terminé ?</legend>
          <label v-for="motif in motifs" :key="motif.id" class="fm-case">
            <input v-model="form.motif_fin" type="radio" name="motif_fin" :value="motif.id"> {{ motif.libelle }}<span v-if="motif.aide" class="fm-precision"> — {{ motif.aide }}</span>
          </label>
        </fieldset>
        <label>Commentaire (facultatif)
          <textarea v-model="form.commentaire" rows="2" maxlength="500" :disabled="is_saving" placeholder="Ex. flacon tombé, résultat du CQ…"></textarea>
        </label>
        <p v-if="erreur" role="alert" class="fm-erreur">{{ erreur }}</p>
        <div class="ui-modal-actions fm-actions">
          <button type="button" class="btn btn-secondary-light-o btn-radius" :disabled="is_saving" @click="fermer">Annuler</button>
          <button type="button" class="btn btn-primary btn-radius" :disabled="is_saving" @click="enregistrerFin">{{ is_saving ? 'Enregistrement…' : 'Terminer le flacon' }}</button>
        </div>
      </div>
    </b-modal>

    <b-modal :id="'flacon-regler-' + _uid" v-model="modal_regler" :title="'Suivi de l’article — ' + libelle" hide-footer
      :no-close-on-backdrop="is_saving" :no-close-on-esc="is_saving">
      <div class="fm-formulaire">
        <fieldset class="fm-motifs" :disabled="is_saving">
          <legend>Comment le laboratoire suit cet article ?</legend>
          <label class="fm-case"><input v-model="form.suivi" type="radio" name="suivi" value=""> Automatique<span class="fm-precision"> — réactifs au flacon, le reste à l'unité</span></label>
          <label class="fm-case"><input v-model="form.suivi" type="radio" name="suivi" value="flacon"> Au flacon ou au kit<span class="fm-precision"> — ouvrir puis terminer</span></label>
          <label class="fm-case"><input v-model="form.suivi" type="radio" name="suivi" value="unite"> À l'unité<span class="fm-precision"> — déclarer les quantités consommées</span></label>
        </fieldset>
        <label>Stabilité après ouverture (jours)
          <input v-model="form.stabilite_jours" type="number" min="1" max="3650" step="1" :disabled="is_saving" placeholder="Ex. 30">
        </label>
        <p v-if="erreur" role="alert" class="fm-erreur">{{ erreur }}</p>
        <div class="ui-modal-actions fm-actions">
          <button type="button" class="btn btn-secondary-light-o btn-radius" :disabled="is_saving" @click="fermer">Annuler</button>
          <button type="button" class="btn btn-primary btn-radius" :disabled="is_saving" @click="enregistrerReglage">{{ is_saving ? 'Enregistrement…' : 'Enregistrer' }}</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import api from '@/api/_stock-laboratoire'
import { MOTIFS_FIN, aujourdhuiLocal, fuseauNavigateur, cleRequete, dateCourte, limiteUtilisation } from './flacons-utils'

const message = (error, defaut) => {
  const data = error && error.response && error.response.data
  if (data && typeof data.message === 'string' && data.message && !/^The given data was invalid/.test(data.message)) return data.message
  if (data && data.errors) {
    const premier = Object.keys(data.errors).map(cle => data.errors[cle])[0]
    if (Array.isArray(premier) && premier[0]) return premier[0]
  }
  return defaut
}

const stabiliteSaisie = valeur => {
  const texte = String(valeur === null || valeur === undefined ? '' : valeur).trim()
  return texte === '' ? null : Number(texte)
}

// Fenêtres des réactifs suivis au flacon (04/10/2026) : ouvrir (lot, péremption, stabilité, CQ), terminer (motif),
// réglages de l'article (mode de suivi, stabilité) ; bascule du contrôle qualité. Émet « enregistre » avec un message.
export default {
  name: 'flacons-modales',
  data() {
    return { fenetre: null, ligne: null, flacon: null, form: {}, erreur: '', is_saving: false, motifs: MOTIFS_FIN, aujourdhui: aujourdhuiLocal() }
  },
  computed: {
    modal_ouvrir: { get() { return this.fenetre === 'ouvrir' }, set(v) { if (!v) this.fermer() } },
    modal_terminer: { get() { return this.fenetre === 'terminer' }, set(v) { if (!v) this.fermer() } },
    modal_regler: { get() { return this.fenetre === 'regler' }, set(v) { if (!v) this.fermer() } },
    libelle() { return this.ligne ? this.ligne.libelle : '' },
    reserve() { return this.ligne ? Number(this.ligne.quantite) || 0 : 0 },
    limite() { return limiteUtilisation(this.aujourdhui, this.form.date_peremption, stabiliteSaisie(this.form.stabilite_jours)) },
  },
  methods: {
    dateCourte,
    afficher(fenetre, ligne, flacon, form) {
      this.aujourdhui = aujourdhuiLocal()
      this.ligne = ligne
      this.flacon = flacon || null
      this.form = form
      this.erreur = ''
      this.fenetre = fenetre
    },
    fermer() {
      if (this.is_saving) return
      this.fenetre = null
    },
    ouvrir(ligne) {
      const dernier = ligne.dernier_flacon || {}
      const peremption = dernier.date_peremption && dernier.date_peremption >= aujourdhuiLocal() ? dernier.date_peremption : ''
      this.afficher('ouvrir', ligne, null, { lot: dernier.lot || '', date_peremption: peremption,
        stabilite_jours: ligne.stabilite_jours || '', cq_fait: false, request_key: cleRequete() })
    },
    terminer(ligne, flacon) {
      this.afficher('terminer', ligne, flacon, { motif_fin: 'epuise', commentaire: '' })
    },
    regler(ligne) {
      this.afficher('regler', ligne, null, { suivi: ligne.suivi_regle || '', stabilite_jours: ligne.stabilite_jours || '' })
    },
    validerStabilite() {
      const stabilite = stabiliteSaisie(this.form.stabilite_jours)
      return stabilite !== null && (!Number.isInteger(stabilite) || stabilite < 1 || stabilite > 3650)
        ? 'La stabilité après ouverture doit être un nombre de jours entre 1 et 3650.' : ''
    },
    async envoyer(action, succes, defaut) {
      if (this.is_saving) return
      this.is_saving = true
      this.erreur = ''
      try {
        const reponse = await action()
        if (reponse && reponse.success === false) {
          const refus = new Error('refus')
          refus.response = { data: reponse }
          throw refus
        }
        this.is_saving = false
        this.fenetre = null
        this.$emit('enregistre', succes)
      } catch (error) {
        this.erreur = message(error, defaut)
      } finally {
        this.is_saving = false
      }
    },
    enregistrerOuverture() {
      const lot = String(this.form.lot || '').trim()
      if (this.form.date_peremption && this.form.date_peremption < this.aujourdhui) {
        this.erreur = 'Ce flacon est périmé : ne l\'ouvrez pas. Déclarez-le en consommation « Périmé ».'
        return
      }
      this.erreur = this.validerStabilite()
      if (this.erreur) return
      const ligne = this.ligne
      const donnees = { article_id: ligne.id, lot: lot || null, date_peremption: this.form.date_peremption || null,
        stabilite_jours: stabiliteSaisie(this.form.stabilite_jours), cq_fait: !!this.form.cq_fait,
        request_key: this.form.request_key, fuseau: fuseauNavigateur() || null }
      return this.envoyer(() => api.ouvrirFlacon(donnees),
        'Flacon ouvert : ' + ligne.libelle + (lot ? ' (lot ' + lot + ')' : '') + '. Réserve : ' + Math.max(0, this.reserve - 1) + '.',
        'Le flacon n\'a pas pu être ouvert : réserve vide ou application Stock indisponible.')
    },
    enregistrerFin() {
      const motif = MOTIFS_FIN.find(m => m.id === this.form.motif_fin)
      if (!motif) { this.erreur = 'Choisissez pourquoi le flacon est terminé.'; return }
      const commentaire = String(this.form.commentaire || '').trim()
      return this.envoyer(() => api.terminerFlacon(this.flacon.id, { motif_fin: motif.id, commentaire: commentaire || null }),
        'Flacon terminé (' + motif.libelle.toLowerCase() + ') : ' + this.ligne.libelle + '.',
        'Le flacon n\'a pas pu être terminé. Actualisez puis réessayez.')
    },
    enregistrerReglage() {
      this.erreur = this.validerStabilite()
      if (this.erreur) return
      const suivi = this.form.suivi === 'flacon' || this.form.suivi === 'unite' ? this.form.suivi : null
      return this.envoyer(() => api.reglerSuivi(this.ligne.id, { suivi, stabilite_jours: stabiliteSaisie(this.form.stabilite_jours) }),
        'Suivi enregistré pour ' + this.ligne.libelle + '.', 'Le réglage n\'a pas pu être enregistré.')
    },
    async basculerCq(ligne, flacon) {
      try {
        await api.controleQualiteFlacon(flacon.id, !flacon.cq_fait)
        this.$emit('enregistre', (flacon.cq_fait ? 'Contrôle qualité retiré : ' : 'Contrôle qualité noté : ') + ligne.libelle + '.')
      } catch (error) {
        this.$emit('erreur', message(error, 'Le contrôle qualité n\'a pas pu être enregistré.'))
      }
    },
  },
}
</script>

<style scoped>
.fm-formulaire { display: flex; flex-direction: column; gap: 12px; color: var(--ui-text, #172033); }
.fm-formulaire label { display: flex; flex-direction: column; gap: 4px; margin: 0; font-weight: 600; font-size: 14px; }
.fm-formulaire input[type="text"], .fm-formulaire input[type="date"], .fm-formulaire input[type="number"], .fm-formulaire textarea {
  min-height: 40px; padding: 8px 10px; border: 1px solid #c3cddd; border-radius: 10px; font-weight: 400; color: var(--ui-text, #172033); background: #fff; }
.fm-formulaire small { color: var(--ui-text-muted, #526074); font-weight: 400; }
.fm-deux { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.fm-aide { margin: 0; color: var(--ui-text-secondary, #273449); }
.fm-formulaire .fm-case { flex-direction: row; align-items: center; gap: 8px; font-weight: 400; }
.fm-precision { color: var(--ui-text-muted, #526074); }
.fm-motifs { margin: 0; padding: 0; border: 0; display: flex; flex-direction: column; gap: 6px; }
.fm-motifs legend { margin-bottom: 4px; font-size: 14px; font-weight: 700; }
.fm-limite { margin: 0; padding: 8px 12px; border-radius: 10px; background: #edf3ff; color: var(--ui-text-secondary, #273449); font-weight: 600; }
.fm-erreur { margin: 0; color: #a01e14; }
.fm-formulaire input:focus-visible, .fm-formulaire textarea:focus-visible { outline: 3px solid #8caaf5; outline-offset: 1px; }
@media (max-width: 575px) { .fm-deux { grid-template-columns: 1fr; } }
</style>
